/* ═══════════════════════════════════════════════════════════════
   THE DESIGNER — the project-side surface.

   Microsoft Word, except it is physically incapable of producing a
   malformed spec. ONE canvas, ONE selection system, ONE document —
   and, since R5, NO MODES.

   The body always feels like writing: click anywhere and type. The
   header, footer, pre-body and post-body bands always feel like the
   forma editor: click a band, drag tokens onto it, bind placeholders,
   inline, without a mode flip. Level styling is a sub-tool you reach
   for (the Levels palette, or right-click ▸ Level style…), never
   something a click on body text triggers.

   Josh's law: MENU ACCESS ALWAYS; DOCKS ARE OPTIONAL. Since §2.8 that
   guarantee is carried by the panel BUTTONS on row 2: every panel is
   one click from its own button, which drops it down anchored, and
   the same button dragged docks or floats it. Nothing in this product
   is reachable only by docking something.

   Ctrl+Z is universal (§2.7): it undoes the last thing the user did,
   text or formatting, without them choosing a kind of undo.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import { Download, History as HistoryIcon, Inbox, Send, Stamp } from 'lucide-react';
import type {
  ContentBlock,
  Issuance,
  ProjectManualIssuance,
  ProjectSpec,
} from '../../shared/types';
import { Prefs } from '../services/prefsService';
import { setRecordTitle } from '../routing/addressSync';
import { readPref, usePref, writePref } from '../lib/usePref';
import EmptyState from '../components/EmptyState';
import { isWeb } from '../lib/platform';
import type { FormaDockHandle } from '../components/FormaDock';
import { DesignerCanvas } from './DesignerCanvas';
import { ZoomControl } from '../components/ui/ZoomControl';
import {
  FindButton,
  FindReplaceHost,
  InsertImageButton,
  InsertRelatedSectionsButton,
  InsertTableButton,
} from './Tools';
import { RelatedSectionsPicker, useRelatedSections } from './RelatedSections';
import { InlineMarks, LevelCluster, UniversalHistory } from '../editor-chrome/ToolbarControls';
import {
  DesignerToolbar,
  ToolbarButton,
  type PanelEntry,
  type ToolbarItem,
} from '../editor-chrome/Toolbar';
import { useDesignerDoc, type SaveState } from './useDesignerDoc';
import { DesignerWorkspace, PANEL_ICONS, designerPanelButtons } from './Workspace';
import { isOverDockZone, type FloatingTool } from '../editor-chrome/dock';
import { ModulesPanel } from '../components/ModulesPanel';
import { useModuleInsert, type ToastOptions } from '../components/useModuleInsert';
import { useUndoCoordinator } from '../editor-chrome/undoCoordinator';
import { docUndoDepth } from '../spec-studio/collabUndo';
import { PeerChips } from '../spec-studio/PeerChips';
import { FirmPrefs } from '../services/firmPrefsService';
import { resolvePlaceholders, usedPlaceholderIds } from '../../shared/forma-placeholders';
import { blocksToDoc } from './schema';
import type { NavItem } from './Navigation';
import { IssuanceDesk, type IssueExportOptions } from './IssuanceDesk';
import { ExportDialog } from './ExportDialog';
import { RevisionStylesPanel } from './RevisionStylesPanel';
import { HistoryPanel } from './HistoryPanel';
import { exportManualSpecs, type ExportFormat } from './exportManual';
import { StatusBadge } from '../components/StatusBadge';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { SyncService } from '../services/syncService';
import { isProjectSpecPending, isProjectSpecPublished } from '../../shared/spec-publish';
import { publishConfirmBody } from '../spec-studio/collabState';
import { useReview } from '../review/useReview';
import { useReviewDecorations } from '../review/useReviewDecorations';
import { ReviewBar } from '../review/ReviewBar';
import { ReviewPanel } from '../review/ReviewPanel';
import {
  MasterUpdatesReminder,
  dismissReminder,
  markReminded,
  shouldRemind,
} from '../review/MasterUpdatesReminder';
import { syncService } from '../review/syncAdapter';
import { formaIntentAction } from '../review/formaIntent';
import { useFormaReview } from '../review/useFormaReview';
import { formaReviewHighlights } from '../review/formaHighlights';
import {
  FormaChip,
  FormaFormatBar,
  formatSpecNumber,
  pageVariants,
  useFormaDraft,
  useFormaSelection,
  discardDraftBody as formaDiscardDraftBody,
  peerNames as formaPeerNames,
  publishConfirmBody as formaPublishConfirmBody,
  type PageVariant,
} from '../forma-studio';
import { Button } from '../components/ui/button';
import type { ReviewPluginInput } from '../review/reviewPlugin';
import { isFormaChange, type Change } from '../../shared/sync';
import { WHOLE_DOC_NOTE } from '../../shared/sync/forma';
import '../editor-chrome/editor.css';

export interface DesignerProps {
  /** The section currently open. */
  projectSpecId: string;
  /** Open a different section (the shell owns routing). */
  onOpenSection: (specId: string) => void;
  /** Leave the Designer. */
  onBack: () => void;
  /** Back to the project's Specs list, where sections are added/removed. */
  onManageSections?: () => void;
  userName: string;
  canEdit: boolean;
  /** project_specs.create_issuance — gates the Issue flow. Defaults to
   *  canEdit when the shell has no finer-grained answer. */
  canIssue?: boolean;
  /** admin_center.manage_firm_settings — gates "Set as firm default", which
   *  changes what every uncustomised colleague sees (contract §2.9). */
  canSetFirmDefault?: boolean;
  /** modules.view — may this user see the firm's module library? Inserting one
   *  rides `canEdit` above; there is no insert-only permission (Phase 5). */
  canViewModules?: boolean;
  /** The app toast; the optional third argument carries an action button
   *  (a module drop's "Show" — app.tsx `toast` already accepts it). */
  toast: (msg: string, isError?: boolean, opts?: ToastOptions) => void;
  /** Theme, for the dockview host. */
  isDark?: boolean;
  /** Scopes the workspace layout and the toolbar to the signed-in user. */
  userId?: string;
  /** The shell opened this document to WORK ON THE PAPER (opening a project
   *  forma from the Formas library). There is no mode to switch into any
   *  more — the workspace simply brings the formatting tools forward. A new
   *  `ts` re-applies the request without remounting the surface. */
  openFormatting?: { ts: number } | null;
  /** Hands the shell a stable "save now" trigger while the Designer is
   *  mounted (null on unmount) — the native File-menu Ctrl+S accelerator
   *  routes through this, same as the toolbar's "Save now" button. This
   *  document autosaves; the trigger just flushes the pending debounce. */
  registerSaveHandler?: (fn: (() => void) | null) => void;
  /** project_specs.view — shows the Master updates button (Review, D6). */
  canViewMasterUpdates?: boolean;
  /** specs.publish — may accept / reject master updates and Finish. Without
   *  it Review is a read-only walk (the server enforces the same key). */
  canAcceptMasterUpdates?: boolean;
  /** specs.publish — shows Publish (the project spec's gate, SYNC D12); the
   *  server enforces the same key. Hidden without it. */
  canPublish?: boolean;
  /** The shell opened this section from the Master updates widget: go
   *  straight into Review once the updates have loaded. A new `ts`
   *  re-applies the request. */
  openReview?: { ts: number } | null;
  /** formas.edit — may edit the project forma's shared draft (Sync appendix
   *  item 9). Defaults to canEdit. */
  canEditForma?: boolean;
  /** formas.publish — Publish / Discard draft on the project forma, and
   *  Review + Accept its master updates (SYNC_PLAN §3.3). The server
   *  enforces the same key. */
  canPublishForma?: boolean;
  /** The shell opened this section to review the PROJECT FORMA's master
   *  updates (the Specs widget's forma badge). A new `ts` re-applies it. */
  openFormaReview?: { ts: number } | null;
}

/** A forma setting's Review row reads in words ("Footer, left: order changed"). */
function describeFormaChange(c: Change): string {
  return isFormaChange(c) ? c.label : c.after;
}

/** Doc-wide settings (page, typography, blank page…) say so under the label;
 *  zone cards glow their zone instead. */
function describeFormaNote(c: Change): string | null {
  return isFormaChange(c) &&
    (c.settingKind === 'page' || c.settingKind === 'special' || c.settingKind === 'placeholder')
    ? WHOLE_DOC_NOTE
    : null;
}

/** Why editing / history controls are inert while Review is open. */
const REVIEW_LOCK_REASON = 'Finish or leave Review to edit';

/** What the canvas is currently showing instead of the live document. */
type Viewing =
  | { kind: 'issuance'; mi: ProjectManualIssuance }
  | { kind: 'snapshot'; snap: Issuance }
  | null;

// Cohesion R1 Phase 4 zoom law: 50–180% everywhere.
// UI Scale (DESIGN.md §3.6): `zoom` here is RELATIVE to the dial (1.0 =
// "matches surroundings") — the canvas multiplies it by the dial to get
// the applied transform. Storage/keys are unchanged: every value saved so
// far was captured at dial 1.00, so a saved number IS the relative value
// already — no migration/conversion needed.
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.8;
const ZOOM_KEY = 'designer.zoom';

/** Automated capture/QA runs sign in as a real user; without this they would
    overwrite that user's CLOUD zoom with a throwaway one (FormaDock.tsx /
    Toolbar.tsx have the same guard for dock layouts). Local persistence
    still works so a harness can test restore within its own profile. */
const cloudPersistDisabled = () => {
  try {
    return localStorage.getItem('sf.dockNoCloudPersist') === '1';
  } catch {
    return false;
  }
};

/** Where the firm's default workspace arrangement lives (contract §2.9).
 *  ONE constant — swapping the app's out-of-box default for a different
 *  starting layout is a one-line change, exactly as the contract asks. */
export const DESIGNER_FIRM_LAYOUT_KEY = 'sf.designerDock.firmDefault.v2';

/** Section last opened per PROJECT forma (contract §3.2). The fast cache is
 *  still one localStorage key per project — untouched, never renamed — but
 *  the CLOUD side is ONE map-valued pref (`{ [projectId]: specId }`, capped
 *  at the 200 most recently written projects), matching the single-map
 *  pattern in spec-studio/useVariant.ts's `VARIANT_KEY`. A per-project cloud
 *  key would mean one `user_prefs` row per project ever opened, unbounded. */
const LAST_SPEC_KEY = 'sf.designer.lastSpec.v1';
const LAST_SPEC_CAP = 200;

function legacyLastSpecLocalKey(projectId: string): string {
  return 'sf.designer.lastSpec.' + projectId;
}

/** Cloud (once ready) wins over the per-project local seed, same
 *  server-wins-once intent as `readPref`. */
export function readLastSpecPref(projectId: string): string | null {
  if (Prefs.isReady()) {
    const map = Prefs.get(LAST_SPEC_KEY) as Record<string, string> | undefined;
    if (map && map[projectId] != null) return map[projectId];
  }
  try {
    const raw = window.localStorage.getItem(legacyLastSpecLocalKey(projectId));
    return raw ? (JSON.parse(raw) as string) : null;
  } catch {
    return null;
  }
}

function writeLastSpecPref(projectId: string, specId: string): void {
  try {
    window.localStorage.setItem(legacyLastSpecLocalKey(projectId), JSON.stringify(specId));
  } catch {
    /* private mode / storage unavailable — the in-memory value still applies */
  }
  const map = readPref<Record<string, string>>(LAST_SPEC_KEY, LAST_SPEC_KEY, {});
  delete map[projectId]; // reinsert below so it becomes the most recent entry
  map[projectId] = specId;
  const keys = Object.keys(map);
  if (keys.length > LAST_SPEC_CAP) {
    for (const stale of keys.slice(0, keys.length - LAST_SPEC_CAP)) delete map[stale];
  }
  writePref(LAST_SPEC_KEY, LAST_SPEC_KEY, map);
}

/** The one status chip: save state first (Saving… / Offline / errors), then
 *  where the section stands against its Publish (Published / Pending
 *  publish / Not published yet). */
function SaveIndicator({
  state,
  at,
  publish,
}: {
  state: SaveState;
  at: Date | null;
  publish: 'published' | 'pending' | 'never';
}) {
  const settled = state === 'idle' || state === 'saved';
  const label =
    state === 'saving'
      ? 'Saving…'
      : state === 'dirty'
        ? 'Unsaved changes'
        : state === 'offline'
          ? 'Offline'
          : state === 'error'
            ? 'Not saved — retrying'
            : publish === 'pending'
              ? 'Pending publish'
              : publish === 'never'
                ? 'Not published yet'
                : 'Published';
  const savedAt = at
    ? ` Last saved ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.`
    : '';
  return (
    <span
      className={`dsg-save dsg-save-${state}${settled && publish === 'pending' ? ' dsg-save-pending' : ''}`}
      role="status"
      aria-live="polite"
      title={`This document saves itself — there is nothing to submit.${savedAt}`}
    >
      {state === 'error' && (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M12 8v5M12 17h.01" />
          <circle cx="12" cy="12" r="9" />
        </svg>
      )}
      {label}
    </span>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="dsg-icon-btn"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

/** Change bars + a delta: the universal "tracked changes" glyph. The
 *  highlighter is reserved for the manual marker pen (§2.4). */
const TrackChangesIcon = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 3v18M7 3v18" />
    <path d="M16 7l5 9h-10z" />
  </svg>
);

export function Designer({
  projectSpecId,
  onOpenSection,
  onBack,
  onManageSections,
  userName,
  canEdit,
  canIssue,
  canSetFirmDefault = false,
  canViewModules = false,
  toast,
  isDark = false,
  userId,
  openFormatting,
  registerSaveHandler,
  canViewMasterUpdates = true,
  canAcceptMasterUpdates = false,
  canPublish = false,
  openReview,
  canEditForma,
  canPublishForma = false,
  openFormaReview,
}: DesignerProps) {
  const doc = useDesignerDoc(projectSpecId, userName, canEdit);
  const [editor, setEditor] = React.useState<Editor | null>(null);
  const topBarRef = React.useRef<HTMLElement>(null);
  const toolbarRef = React.useRef<HTMLDivElement>(null);
  const mayIssue = canIssue ?? canEdit;

  // The spec's content changed outside the editor (an accepted module update,
  // an import...): the live document was reset server-side. Leave so the next
  // open seeds from the fresh content.
  const collabReset = doc.collabReset;
  const leftForResetRef = React.useRef(false);
  React.useEffect(() => {
    if (!collabReset || leftForResetRef.current) return;
    leftForResetRef.current = true;
    toast('Document refreshed with outside changes');
    onBack();
  }, [collabReset, toast, onBack]);

  // ── Review surfaces ──
  const [deskOpen, setDeskOpen] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [marksAnchor, setMarksAnchor] = React.useState<{
    left: number;
    bottom: number;
    right: number;
  } | null>(null);
  const [historyAnchor, setHistoryAnchor] = React.useState<{
    left: number;
    bottom: number;
    right: number;
  } | null>(null);
  const [viewing, setViewing] = React.useState<Viewing>(null);
  const [exportingIssuance, setExportingIssuance] = React.useState(false);
  const pendingRestoreRef = React.useRef<Issuance | null>(null);

  // Switching sections drops a per-section snapshot view; an ISSUANCE view
  // follows you across the rail — the whole manual as it was issued.
  React.useEffect(() => setViewing((v) => (v?.kind === 'snapshot' ? null : v)), [projectSpecId]);

  // A queued restore applies once the LIVE editor is back (setContent is a
  // normal transaction — it lands in the undo history and autosaves).
  React.useEffect(() => {
    if (!editor || viewing || !pendingRestoreRef.current) return;
    const snap = pendingRestoreRef.current;
    pendingRestoreRef.current = null;
    editor.commands.setContent(blocksToDoc(snap.snapshot?.coreContent || []), true);
    toast('Snapshot restored — press Ctrl+Z to undo');
  }, [editor, viewing, toast]);

  /* ─── UNIVERSAL UNDO (§2.7) ───
     One user-facing timeline over two histories. The coordinator records the
     ORDER actions happened in; Ctrl+Z asks whichever domain owns the most
     recent step. Nobody has to know there are two. */
  const undoTimeline = useUndoCoordinator();
  const noteFormaAction = undoTimeline.note;

  // The forma draft — zone and level edits land here, then write through the
  // existing model (project copy in place; master → copy-on-write fork).
  const draft = useFormaDraft(
    doc.forma,
    doc.spec ? { id: doc.spec.projectId, number: doc.projectNumber, name: doc.projectName } : null,
    userName,
    userId,
    (msg) => toast(msg, true),
    React.useCallback(() => noteFormaAction('forma'), [noteFormaAction]),
    {
      canEdit: canEditForma ?? canEdit,
      canPublish: canPublishForma,
      onEnded: ({ reset, own }) =>
        toast(
          own
            ? 'Forma draft discarded'
            : reset
              ? 'This forma was refreshed - showing the published version'
              : 'A teammate ended this forma draft - showing the published version'
        ),
    }
  );
  // The paper always renders the newest formatting truth.
  const paperForma = draft.forma ?? doc.forma;

  /* ─── Inserting a module (Phase 5) ───
     A project spec is a hand-managed COPY, so an insert here is exactly what
     it is anywhere else: stamped blocks, tagged with the module and version,
     never a live link. Seeing the library is `modules.view`; dropping one in
     rides `project_specs.edit_own` / `edit_any` (this surface's `canEdit`),
     and it saves through the ordinary autosave with no new endpoint.
     The Designer has no gutter selection layer, so "Select insertion" selects
     the run you clicked and says so when the insertion reaches further. */
  const moduleInsert = useModuleInsert({
    editor,
    canEdit,
    canView: canViewModules,
    forma: paperForma,
    toast,
    /* R2 Phase 3, Ruling 3 — "library updates are never silent": a project
       spec learns when a module it carries moves, even though nothing ever
       PRs into one. There is no Updates panel here, so the offer is answered
       WHOLE — take it or keep this copy — in a dialog of its own, and taking
       it is ordinary draft editing on this surface's own `canEdit`
       (project_specs.edit_own / edit_any, server-enforced on save). */
    updatesEnabled: true,
    reviewMode: 'dialog',
  });

  // Token values follow the DRAFT: editing a placeholder binding in the
  // palette re-resolves it on the paper immediately. Stale placeholder ids
  // are stripped first, so unbinding one shows its unbound state again.
  const paperMeta = React.useMemo(() => {
    if (!paperForma) return doc.projectMeta;
    const meta = { ...doc.projectMeta };
    for (const id of usedPlaceholderIds(paperForma)) delete meta[id];
    Object.assign(
      meta,
      resolvePlaceholders(paperForma, {
        project: meta,
        issuances: doc.spec?.issuances || [],
        dateFormat: paperForma.docSettings?.dateFormat,
      })
    );
    Object.assign(meta, doc.spec?.tokenOverrides || {});
    return meta;
  }, [paperForma, doc.projectMeta, doc.spec]);

  /* ─── The two histories, joined ───
     The document's history lives in ProseMirror; the forma's lives in the
     draft. Each keeps its own — the coordinator only learns the ORDER. */
  React.useEffect(() => {
    undoTimeline.register('forma', { undo: draft.undo, redo: draft.redo });
    return () => undoTimeline.register('forma', null);
  }, [undoTimeline, draft.undo, draft.redo]);

  React.useEffect(() => {
    if (!editor) return;
    undoTimeline.register('doc', {
      undo: () => editor.chain().focus().undo().run(),
      redo: () => editor.chain().focus().redo().run(),
    });
    // A new step landing in ProseMirror's history IS a user action on the
    // document. Reading the depth is how we tell a real edit from a
    // selection move, an undo, or a decoration rebuild.
    let depth = docUndoDepth(editor.state);
    const onTx = () => {
      const next = docUndoDepth(editor.state);
      if (next > depth) undoTimeline.note('doc');
      depth = next;
    };
    editor.on('transaction', onTx);
    return () => {
      editor.off('transaction', onTx);
      undoTimeline.register('doc', null);
    };
  }, [editor, undoTimeline]);

  // A different section is a different document — and a different timeline.
  const resetTimeline = undoTimeline.reset;
  React.useEffect(() => resetTimeline(), [projectSpecId, resetTimeline]);

  /* Ctrl+Z / Ctrl+Y, once, for the whole surface. Captured before ProseMirror
     sees them so the caret being in the paper never means "only text undoes".
     Real form fields keep their own native undo — retyping in a text box is
     not a document action. */
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;
      const target = e.target as HTMLElement | null;
      if (target && /^(input|textarea)$/i.test(target.tagName)) return;
      e.preventDefault();
      e.stopPropagation();
      if (isUndo) undoTimeline.undo();
      else undoTimeline.redo();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [undoTimeline]);

  /* ─── The workspace's panels: dropped down, docked, floating, or closed ───
     Josh's law lives here. Every panel is always one click from its top-bar
     button; docking is convenience. `openTool` is the ONE path every
     contextual entry point uses — clicking a band, right-click ▸ Level
     style… — and it never forces a dock. */
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);
  const [dockPanels, setDockPanels] = React.useState<string[]>([]);
  const [floatingTools, setFloatingTools] = React.useState<FloatingTool[]>([]);
  const [dropdown, setDropdown] = React.useState<{
    id: string;
    anchor: { left: number; bottom: number };
  } | null>(null);
  const [panelDragId, setPanelDragId] = React.useState<string | null>(null);

  // A panel the user dragged into the dock stops being a floating panel.
  React.useEffect(() => {
    setFloatingTools((f) => {
      const next = f.filter((t) => !dockPanels.includes(t.id));
      return next.length === f.length ? f : next;
    });
    setDropdown((d) => (d && dockPanels.includes(d.id) ? null : d));
  }, [dockPanels]);

  const floatTool = React.useCallback((id: string, at?: { left: number; top: number } | null) => {
    setDropdown((d) => (d?.id === id ? null : d));
    setFloatingTools((f) => (f.some((t) => t.id === id) ? f : [...f, { id, at }]));
  }, []);

  const openTool = React.useCallback(
    (id: string) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      floatTool(id);
    },
    [dockPanels, floatTool]
  );

  const closeFloating = React.useCallback(
    (id: string) => setFloatingTools((f) => f.filter((t) => t.id !== id)),
    []
  );

  const dockFloating = React.useCallback((id: string) => {
    setFloatingTools((f) => f.filter((t) => t.id !== id));
    dockHandleRef.current?.showPanel(id);
  }, []);

  /* CLICK a panel button: show the panel where it already lives, or drop it
     down anchored under the button. Clicking the button of an open dropdown
     closes it — a toggle, like every other button on this row. */
  const onPanelClick = React.useCallback(
    (id: string, anchor: { left: number; bottom: number }) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      if (floatingTools.some((t) => t.id === id)) {
        closeFloating(id);
        return;
      }
      setDropdown((d) => (d?.id === id ? null : { id, anchor }));
    },
    [dockPanels, floatingTools, closeFloating]
  );

  /* DRAG a panel button: into the dock it docks (FormaDock already accepts
     this payload); released outside every dock zone it floats where it
     landed. */
  const onPanelDragEnd = React.useCallback(
    (id: string, x: number, y: number) => {
      setPanelDragId(null);
      if (isOverDockZone(x, y)) return; // the dock took it
      floatTool(id, { left: x, top: y });
    },
    [floatTool]
  );

  /* A DOCKED panel dragged out of every dock zone becomes a floating box. */
  const undockToFloat = React.useCallback(
    (id: string, at: { left: number; top: number }) => {
      dockHandleRef.current?.hidePanel(id);
      floatTool(id, at);
    },
    [floatTool]
  );

  /* Publish this arrangement as the firm's default (§2.9). Admin-gated at
     the button AND at the server — this changes what every colleague who
     has never arranged their own workspace opens into, so it says so. */
  const setFirmDefault = React.useCallback(() => {
    const layout = dockHandleRef.current?.snapshotLayout();
    if (!layout) {
      toast('The workspace is still opening — try again in a moment.', true);
      return;
    }
    FirmPrefs.set(DESIGNER_FIRM_LAYOUT_KEY, layout, userName)
      .then(() =>
        toast('Saved as the firm default — everyone who has not customised will see this layout')
      )
      .catch((err: unknown) =>
        toast(
          'Could not save the firm default: ' +
            (err instanceof Error ? err.message : String(err ?? 'unknown')),
          true
        )
      );
  }, [toast, userName]);

  const panelEntries = React.useMemo<PanelEntry[]>(
    () =>
      designerPanelButtons(canViewModules).map((p) => ({
        id: p.id,
        title: p.title,
        icon: PANEL_ICONS[p.id] ?? null,
        where: dockPanels.includes(p.id)
          ? 'docked'
          : floatingTools.some((t) => t.id === p.id)
            ? 'floating'
            : dropdown?.id === p.id
              ? 'dropdown'
              : null,
        // Chunks waiting for a spot die with the session, so the count has
        // to be visible from the toolbar even with the panel closed.
        ...(p.id === 'modules' && moduleInsert.chips.length > 0
          ? { badge: moduleInsert.chips.length }
          : {}),
      })),
    [dockPanels, floatingTools, dropdown, canViewModules, moduleInsert.chips]
  );

  // ── The ONE selection (Forma Studio's useFormaSelection) ──
  // Levels, header/footer tokens, End of Section and the targeted band — one
  // state shared by the paper, the forma panels and the Format Bar. Clears
  // on an empty-paper click, Escape, or opening another section's forma.
  const sel = useFormaSelection(paperForma);
  const clearSelection = sel.clear;
  React.useEffect(() => clearSelection(), [draft.formaId, projectSpecId, clearSelection]);

  // ── The Levels sub-tool ──
  // The panel follows the caret unless the user pointed it somewhere
  // deliberately. Only a deliberate pick lights the paper, so typing never
  // flashes highlights across the page.
  const selectLevel = sel.selectLevel;
  const openLevelStyle = React.useCallback(
    (level: number) => {
      selectLevel(level);
      openTool('levels');
    },
    [openTool, selectLevel]
  );

  // Which page variant the zone tools are editing; clamped to what the
  // forma actually has.
  const [pageVariant, setPageVariant] = React.useState<PageVariant>('odd');
  const pvs = pageVariants(paperForma);
  const effPageVariant = pvs.includes(pageVariant) ? pageVariant : 'odd';

  const [tokenDrag, setTokenDrag] = React.useState(false);
  const [dockLocked, setDockLocked] = usePref<boolean>(
    'sf.designerDockLocked',
    'sf.designerDockLocked',
    true,
    { parse: (raw) => raw !== '0', serialize: (v) => (v ? '1' : '0') }
  );
  const toggleDockLocked = React.useCallback(() => setDockLocked((v) => !v), [setDockLocked]);
  // Workspace ▸ View ▸ Pages / Continuous — per user, Continuous by default.
  const [paged, setPaged] = usePref<boolean>('sf.designerPaged', 'sf.designerPaged', false, {
    parse: (raw) => raw === '1',
    serialize: (v) => (v ? '1' : '0'),
  });

  // Pages view shows the true page: page 1 of a first-page-different forma
  // wears the First band, so ENTERING Pages mode defaults the edited variant
  // to `first` (not `odd`). After that the picker is free — the top band
  // always shows the variant the user picked. Continuous is untouched.
  const prevPagedRef = React.useRef(paged);
  React.useEffect(() => {
    if (paged && !prevPagedRef.current && pvs.includes('first')) setPageVariant('first');
    prevPagedRef.current = paged;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paged]);

  // Opening a project forma from the Formas library: no mode to enter, just
  // bring the paper's tools forward.
  React.useEffect(() => {
    if (!openFormatting) return;
    openTool('typography');
    // Re-apply on every new request timestamp, not on object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFormatting?.ts]);

  // Esc clears the zone selection and the level focus (DESIGN §3.4 —
  // a selection is never sticky). The editor's own Esc still blurs it.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      clearSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [clearSelection]);

  // Remember the section last opened per project — opening this project's
  // FORMA later previews against it (contract §3.2: real content, always).
  // D1 cross-surface parity: cloud `user_prefs` + localStorage fast cache,
  // same key doubling as both pref + local key (dynamic per project, so the
  // plain write-through pair is used instead of the `usePref` hook).
  React.useEffect(() => {
    const pid = doc.spec?.projectId;
    if (!pid) return;
    writeLastSpecPref(pid, projectSpecId);
  }, [doc.spec?.projectId, projectSpecId]);

  const [zoom, setZoom] = React.useState<number>(() => {
    const stored = Prefs.get(ZOOM_KEY) ?? Number(localStorage.getItem(ZOOM_KEY));
    const n = Number(stored);
    return Number.isFinite(n) && n >= ZOOM_MIN && n <= ZOOM_MAX ? n : 1;
  });
  // Re-render when prefs land from the server so the layout follows the user
  // across devices rather than being pinned to this machine's localStorage.
  React.useEffect(
    () =>
      Prefs.subscribe(() => {
        const z = Number(Prefs.get(ZOOM_KEY));
        if (Number.isFinite(z) && z >= ZOOM_MIN && z <= ZOOM_MAX) setZoom(z);
      }),
    []
  );

  const setZoomPersisted = (next: number) => {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(next.toFixed(2))));
    setZoom(clamped);
    if (!cloudPersistDisabled()) Prefs.set(ZOOM_KEY, clamped);
    try {
      localStorage.setItem(ZOOM_KEY, String(clamped));
    } catch {
      /* see above */
    }
  };

  const onChange = React.useCallback(
    (blocks: ContentBlock[]) => doc.onBlocksChanged(blocks),
    [doc]
  );

  // Save / Back / another section: the document AND the forma. Edits made
  // on a master forma wait for the project copy's shared document; the
  // status row says so while they land, and a forma that cannot take them
  // keeps the user here with the reason (nothing lost silently).
  const [formaFlushing, setFormaFlushing] = React.useState(false);
  const docFlush = doc.flush;
  const draftFlush = draft.flush;
  const draftQueued = draft.queued;
  const flushAll = React.useCallback(async (): Promise<boolean> => {
    if (draftQueued) setFormaFlushing(true);
    try {
      await Promise.all([docFlush(), draftFlush()]);
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save — try again', true);
      return false;
    } finally {
      setFormaFlushing(false);
    }
  }, [docFlush, draftFlush, draftQueued, toast]);
  const onSave = React.useCallback(() => {
    void flushAll().then((ok) => ok && toast('Saved'));
  }, [flushAll, toast]);
  const onSaveRef = React.useRef(onSave);
  onSaveRef.current = onSave;
  React.useEffect(() => {
    registerSaveHandler?.(() => onSaveRef.current());
    return () => registerSaveHandler?.(null);
  }, [registerSaveHandler]);

  // Leaving flushes first — an edit made a second ago is already on the row.
  const guardModuleChips = moduleInsert.guardLeave;
  const leave = React.useCallback(async () => {
    // Chunks still waiting for a spot are session state — say so before they
    // are lost. Confirming runs this same flush-then-leave.
    if (
      guardModuleChips(() => {
        void flushAll().then((ok) => ok && onBack());
      })
    ) {
      return;
    }
    if (await flushAll()) onBack();
  }, [flushAll, onBack, guardModuleChips]);

  const openSection = React.useCallback(
    async (id: string) => {
      if (id === projectSpecId) return;
      if (await flushAll()) onOpenSection(id);
    },
    [flushAll, onOpenSection, projectSpecId]
  );

  // Navigation ▸ a heading: scroll the paper to it and put the caret in it.
  const jumpTo = React.useCallback(
    (item: NavItem) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .setTextSelection(item.pos + 1)
        .run();
      editor.view.dom
        .querySelector(`[data-block-id="${item.id}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    [editor]
  );

  // ── What the canvas shows while viewing history ──
  // For an issuance view: this section's frozen snapshot from that manual
  // issuance. undefined = the section was not part of it.
  const viewState = React.useMemo<{
    blocks: ContentBlock[] | null;
    key: string;
    label: string;
    missing?: boolean;
  } | null>(() => {
    if (!viewing) return null;
    if (viewing.kind === 'snapshot') {
      const s = viewing.snap;
      return {
        blocks: s.snapshot?.coreContent || [],
        key: `snap:${s.id}`,
        label: `Snapshot · ${new Date(s.createdAt || 0).toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}`,
      };
    }
    const mi = viewing.mi;
    const entry = mi.manifest.find((m) => m.specId === projectSpecId);
    const specRow = doc.exportCtx?.allSpecs.find((s) => s.id === projectSpecId);
    const iss = entry
      ? (specRow?.issuances || []).find((i) => i.id === entry.specIssuanceId)
      : undefined;
    return {
      blocks: iss?.snapshot?.coreContent || null,
      key: `iss:${mi.id}`,
      label: `${mi.name} · as issued`,
      missing: !iss?.snapshot,
    };
  }, [viewing, projectSpecId, doc.exportCtx]);

  /* ── Review: master updates waiting on this section (SYNC_PLAN §2.3) ──
     A mode beside the live document: while it is open the paper is
     read-only (decorations only) and the editing controls are inert. */
  const review = useReview({
    targetKind: 'project_spec',
    targetId: canViewMasterUpdates ? projectSpecId : null,
    service: syncService,
    editor,
    canDecide: canAcceptMasterUpdates,
    toast,
    isLiveDocument: !viewState,
  });
  /* The project FORMA's master updates (SYNC_PLAN §3.2): the same Review
     bar and panel; the paper highlights the changed bands / levels. */
  const formaReviewTarget =
    canViewMasterUpdates && draft.isProjectCopy && draft.live ? draft.formaId : null;
  const formaReview = useFormaReview({
    targetId: formaReviewTarget,
    service: syncService,
    ydoc: draft.ydoc,
    current: draft.docForma,
    canDecide: canPublishForma,
    toast,
    userName,
    userId,
  });
  const specReviewing = review.active;
  const formaReviewing = formaReview.active;
  const reviewing = specReviewing || formaReviewing;
  const reviewLeave = review.leave;
  const formaReviewLeave = formaReview.leave;
  const formaReviewStart = formaReview.start;
  const formaCurrentId = formaReview.walker.current?.id ?? null;
  const formaHighlights = React.useMemo(
    () => (formaReviewing ? formaReviewHighlights(formaReview.items, formaCurrentId) : null),
    [formaReviewing, formaReview.items, formaCurrentId]
  );
  // Review is its own screen (T4): docks and floating tools step aside, the
  // saved layout is held untouched, and everything returns on exit.
  const floatingStashRef = React.useRef<FloatingTool[] | null>(null);
  const floatingNowRef = React.useRef(floatingTools);
  floatingNowRef.current = floatingTools;
  // `dockReady` retries the suspend when review starts before the dock mounts.
  const [dockReady, setDockReady] = React.useState(false);
  React.useEffect(() => {
    if (!reviewing || !dockReady) return;
    dockHandleRef.current?.suspend();
    floatingStashRef.current = floatingNowRef.current;
    setFloatingTools([]);
    return () => {
      dockHandleRef.current?.resume();
      const stash = floatingStashRef.current;
      floatingStashRef.current = null;
      if (stash) setFloatingTools(stash);
    };
  }, [reviewing, dockReady]);
  // A different section is a different review.
  React.useEffect(() => reviewLeave(), [projectSpecId, reviewLeave]);
  const reviewCurrent = review.walker.current;
  const reviewInput = React.useMemo<ReviewPluginInput | null>(
    () =>
      specReviewing && !viewState
        ? {
            changes: review.items,
            decisions: review.decisions,
            currentId: reviewCurrent?.id ?? null,
          }
        : null,
    [specReviewing, viewState, review.items, review.decisions, reviewCurrent]
  );
  useReviewDecorations(editor, reviewInput);
  const reviewStart = review.start;
  // Review owns the live editor: a snapshot view swaps the editor, so it is
  // closed first, and History / mark styles stay inert until Review ends.
  const startReview = React.useCallback(() => {
    setViewing(null);
    setHistoryAnchor(null);
    setMarksAnchor(null);
    formaReviewLeave();
    reviewStart();
  }, [reviewStart, formaReviewLeave]);
  // One Review at a time: the forma's walk closes the section's.
  const startFormaReview = React.useCallback(() => {
    setViewing(null);
    setHistoryAnchor(null);
    setMarksAnchor(null);
    reviewLeave();
    formaReviewStart();
  }, [reviewLeave, formaReviewStart]);

  // From the Specs widget's forma badge: straight into the forma's Review
  // once its updates have loaded.
  const formaIntentRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    const action = formaIntentAction({
      intentTs: openFormaReview?.ts ?? null,
      handledTs: formaIntentRef.current,
      canView: canViewMasterUpdates,
      live: draft.live,
      loaded: formaReview.loaded,
      rowCount: formaReview.rows.length,
    });
    if (action === 'idle' || action === 'wait' || !openFormaReview) return;
    formaIntentRef.current = openFormaReview.ts;
    markReminded(projectSpecId, review.rows);
    if (action === 'start') startFormaReview();
  }, [
    openFormaReview,
    canViewMasterUpdates,
    formaReview.loaded,
    formaReview.rows,
    draft.live,
    projectSpecId,
    startFormaReview,
  ]);

  // ── The project forma's Publish gate (appendix item 9) ──
  const [formaPublishOpen, setFormaPublishOpen] = React.useState(false);
  const [formaDiscardOpen, setFormaDiscardOpen] = React.useState(false);
  const draftPublish = draft.publish;
  const confirmFormaPublish = React.useCallback(async () => {
    setFormaPublishOpen(false);
    try {
      await draftPublish();
      toast('Forma published — exports use it from now on');
    } catch (err) {
      toast(
        'Could not publish the forma: ' +
          (err instanceof Error ? err.message : 'try again in a moment'),
        true
      );
    }
  }, [draftPublish, toast]);
  const reviewPending = review.pendingCount;

  // From the Master updates widget: straight into Review once the updates
  // have loaded (the reminder is skipped — they are already looking).
  const reviewIntentRef = React.useRef<number | null>(null);
  const reviewIntentPending =
    !!openReview && openReview.ts !== reviewIntentRef.current && canViewMasterUpdates;
  React.useEffect(() => {
    const action = formaIntentAction({
      intentTs: openReview?.ts ?? null,
      handledTs: reviewIntentRef.current,
      canView: canViewMasterUpdates,
      live: true,
      loaded: review.loaded,
      rowCount: review.rows.length,
    });
    if (action === 'idle' || action === 'wait' || !openReview) return;
    reviewIntentRef.current = openReview.ts;
    markReminded(projectSpecId, [...review.rows, ...formaReview.rows]);
    if (action === 'start') startReview();
  }, [
    openReview,
    canViewMasterUpdates,
    review.loaded,
    review.rows,
    formaReview.rows,
    projectSpecId,
    startReview,
  ]);

  // ── Publish gate (SYNC D12): exports and issuances read the PUBLISHED copy ──
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [publishBusy, setPublishBusy] = React.useState(false);
  const specPending = doc.spec ? isProjectSpecPending(doc.spec) : false;
  const specEverPublished = doc.spec ? isProjectSpecPublished(doc.spec) : false;
  const publishStanding: 'published' | 'pending' | 'never' = !specEverPublished
    ? 'never'
    : specPending
      ? 'pending'
      : 'published';
  const publishFlush = doc.flush;
  const publishSpecId = doc.spec?.id ?? null;
  const confirmPublish = React.useCallback(async () => {
    if (!publishSpecId) return;
    setPublishBusy(true);
    try {
      // Pending keystrokes go down first — Publish copies what is saved.
      await publishFlush();
      await SyncService.publishProjectSpec(publishSpecId);
      toast('Published — exports and issues now use this version');
      setPublishOpen(false);
    } catch (err) {
      toast(
        'Could not publish: ' + (err instanceof Error ? err.message : 'try again in a moment'),
        true
      );
    } finally {
      setPublishBusy(false);
    }
  }, [publishFlush, publishSpecId, toast]);

  // The reminder on open (D7): once per session per section; "Don't show
  // again" holds until a different update arrives.
  const [reminderOpen, setReminderOpen] = React.useState(false);
  React.useEffect(() => {
    if (
      !canViewMasterUpdates ||
      review.loading ||
      reviewIntentPending ||
      (!!formaReviewTarget && !formaReview.loaded) ||
      reviewing
    )
      return;
    if (!shouldRemind(projectSpecId, review.rows, reviewPending)) return;
    markReminded(projectSpecId, review.rows);
    setReminderOpen(true);
  }, [
    canViewMasterUpdates,
    projectSpecId,
    review.rows,
    formaReview.rows,
    reviewPending,
    review.loading,
    reviewIntentPending,
    formaReview.loaded,
    formaReviewTarget,
    reviewing,
  ]);

  // ── Related sections: the live project list behind the block, its
  //    picker, and its insert (seeded with the project's sections). The
  //    edit gate is this surface's own (project_specs.edit_own/edit_any). ──
  const related = useRelatedSections({
    editor,
    thisSpecId: projectSpecId,
    projectSpecs: doc.projectSpecs,
    ready: doc.projectSpecsReady,
    canEdit: canEdit && !viewState,
  });

  /* ── Export an issuance exactly as it existed ──
     Used both from the timeline ("Export…" on a past issuance) and as the
     file half of the issue flow itself (§2.10). `fresh` carries the rows
     that were just frozen, so issuing does not have to wait for the reload
     to land in state before it can produce the documents. */
  const exportIssuance = React.useCallback(
    async (
      mi: ProjectManualIssuance,
      opts?: { fresh?: ProjectSpec[]; format?: ExportFormat; marked?: boolean }
    ) => {
      if (!doc.exportCtx) {
        toast('Still loading the project — try again in a moment.', true);
        return;
      }
      setExportingIssuance(true);
      try {
        // Freshly-issued rows supersede whatever the loaded context holds.
        const pool = opts?.fresh?.length
          ? [
              ...opts.fresh,
              ...doc.exportCtx.allSpecs.filter((s) => !opts.fresh!.some((f) => f.id === s.id)),
            ]
          : doc.exportCtx.allSpecs;
        const overrides = new Map<
          string,
          {
            coreContent: ContentBlock[];
            systems?: any[];
            tokenOverrides?: Record<string, string>;
            csiNumber?: string;
            title?: string;
          }
        >();
        const targets: ProjectSpec[] = [];
        for (const entry of mi.manifest) {
          const spec = pool.find((s) => s.id === entry.specId);
          const iss = spec
            ? (spec.issuances || []).find((i) => i.id === entry.specIssuanceId)
            : undefined;
          if (!spec || !iss?.snapshot) continue;
          // Marked export: the diff baseline is the issuance BEFORE this one,
          // so hide the one we are exporting from the row the pipeline reads.
          // (Content still comes from the snapshot override below, so what
          //  prints is exactly what was sealed.)
          targets.push(
            opts?.marked
              ? { ...spec, issuances: (spec.issuances || []).filter((i) => i.id !== iss.id) }
              : spec
          );
          overrides.set(spec.id, {
            coreContent: iss.snapshot.coreContent || [],
            systems: iss.snapshot.systems || [],
            tokenOverrides: iss.snapshot.tokenOverrides,
            csiNumber: iss.snapshot.csiNumber,
            title: iss.snapshot.title,
          });
        }
        if (!targets.length) {
          toast('No snapshots survive for that issuance.', true);
          return;
        }
        const res = await exportManualSpecs({
          specs: targets,
          ctx: { ...doc.exportCtx, allSpecs: pool },
          marked: !!opts?.marked,
          format: opts?.format ?? 'docx',
          isWeb,
          snapshotOverrides: overrides,
          issuanceOverride: { name: mi.name, date: mi.date },
        });
        if (res.kind === 'blocked') {
          toast(
            `Set ${res.placeholders.join(', ')} in the forma's Project Tokens before exporting.`,
            true
          );
        } else if (res.kind === 'done') {
          if (res.failures.length) {
            toast(`Exported with ${res.failures.length} failure(s) — see console.`, true);
            console.warn('[designer-export-issuance] failures:', res.failures);
          } else {
            toast(`Exported “${mi.name}” — ${res.ok} file${res.ok === 1 ? '' : 's'}`);
          }
        }
      } catch (err) {
        toast(
          'Export failed: ' + (err instanceof Error ? err.message : String(err ?? 'unknown')),
          true
        );
      } finally {
        setExportingIssuance(false);
      }
    },
    [doc.exportCtx, toast]
  );

  /* ── Issue = RECORD **and** EXPORT (§2.10) ──
     The record goes down first: it is the thing that must not be lost, and a
     set that was recorded but not exported can still be exported from the
     timeline. A set that was exported but never recorded cannot be
     un-shipped. */
  const issueAndExport = React.useCallback(
    async (
      meta: { name: string; date?: string; notes?: string },
      sectionIds: string[],
      options: IssueExportOptions
    ) => {
      const { issuance, specs } = await doc.issueManual(meta, sectionIds);
      toast(
        `Issued “${issuance.name}” — ${sectionIds.length} section${
          sectionIds.length === 1 ? '' : 's'
        }. Saving the files…`
      );
      await exportIssuance(issuance, {
        fresh: specs,
        format: options.format,
        marked: options.marked,
      });
    },
    [doc, exportIssuance, toast]
  );

  const spec = doc.spec;

  // Tab / window title: "<number> <title> · <project> · SpecForma".
  const recordName = [spec?.csiNumber, spec?.title].filter(Boolean).join(' ') || 'Untitled section';
  React.useEffect(() => {
    setRecordTitle(recordName);
    return () => setRecordTitle(null);
  }, [recordName]);

  /* ─── Row 2: the toolbar ───
     CORE items can never be unpinned; everything else the user pins and
     unpins from Workspace ▸ Customise toolbar… or by right-clicking the row. */
  const toolbarItems = React.useMemo<ToolbarItem[]>(() => {
    if (!spec) return [];
    const editable = canEdit && !viewState && !doc.collabPending && !reviewing;
    return [
      {
        id: 'undo-redo',
        label: 'Undo and redo',
        core: true,
        section: 'editing',
        node: <UniversalHistory timeline={undoTimeline} editor={editor} />,
      },
      {
        id: 'zoom',
        label: 'Zoom',
        core: true,
        section: 'editing',
        node: (
          <ZoomControl
            value={Math.round(zoom * 100)}
            onChange={(pct) => setZoomPersisted(pct / 100)}
            min={Math.round(ZOOM_MIN * 100)}
            max={Math.round(ZOOM_MAX * 100)}
          />
        ),
      },
      {
        id: 'save',
        label: 'Save now',
        core: true,
        section: 'document',
        node: (
          <ToolbarButton
            label="Save now"
            variant={canPublish ? 'outline' : 'default'}
            onClick={onSave}
            disabled={!editable}
          />
        ),
      },
      ...(canPublish
        ? [
            {
              id: 'publish',
              label: 'Publish',
              core: true,
              section: 'document' as const,
              node: (
                <ToolbarButton
                  icon={Send}
                  label="Publish"
                  variant="default"
                  tooltip={
                    reviewing
                      ? REVIEW_LOCK_REASON
                      : specPending || !specEverPublished
                        ? 'Make this version the one that exports and issues'
                        : 'Nothing to publish — this section is up to date'
                  }
                  disabled={reviewing || !!viewState || (specEverPublished && !specPending)}
                  onClick={() => setPublishOpen(true)}
                />
              ),
            },
          ]
        : []),
      {
        id: 'level',
        label: 'Paragraph level',
        section: 'editing',
        node: <LevelCluster editor={editor} canEdit={editable} onLevelStyle={openLevelStyle} />,
      },
      {
        id: 'marks',
        label: 'Bold, italic, underline, highlight',
        section: 'editing',
        node: <InlineMarks editor={editor} canEdit={editable} />,
      },
      {
        id: 'find',
        label: 'Find & replace',
        section: 'editing',
        node: <FindButton editor={editor} />,
      },
      {
        id: 'insert',
        label: 'Insert image, table and related sections',
        section: 'editing',
        node: (
          <div className="dsg-group" role="group" aria-label="Insert">
            <InsertImageButton editor={editor} canEdit={editable} />
            <InsertTableButton editor={editor} canEdit={editable} />
            <InsertRelatedSectionsButton
              editor={editor}
              canEdit={editable}
              onInsert={related.insert}
            />
          </div>
        ),
      },
      {
        // Ctrl+Z now covers formatting too (§2.7); this pair stays as the
        // visual affordance for "step back through FORMATTING only",
        // which is still a thing people reach for while restyling.
        id: 'forma-history',
        label: 'Undo and redo formatting only',
        section: 'editing',
        node: (
          <div className="dsg-group" role="group" aria-label="Formatting history">
            <IconButton
              label="Undo formatting change only"
              onClick={() => void draft.undo()}
              disabled={!draft.canUndo}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 7v6h6" />
                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                <path d="M9 20h9" />
              </svg>
            </IconButton>
            <IconButton
              label="Redo formatting change only"
              onClick={() => void draft.redo()}
              disabled={!draft.canRedo}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 7v6h-6" />
                <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
                <path d="M6 20h9" />
              </svg>
            </IconButton>
          </div>
        ),
      },
      {
        id: 'track-changes',
        label: 'Track changes — revision marks',
        section: 'editing',
        node: (
          <ToolbarButton
            iconNode={TrackChangesIcon}
            label="Track changes"
            tooltip={reviewing ? REVIEW_LOCK_REASON : 'How revision marks print'}
            disabled={reviewing}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setMarksAnchor({ left: r.left, bottom: r.bottom, right: r.right });
            }}
          />
        ),
      },
      ...(canViewMasterUpdates
        ? [
            {
              id: 'master-updates',
              label: 'Master updates',
              section: 'document' as const,
              node: (
                <ToolbarButton
                  icon={Inbox}
                  label="Master updates"
                  tooltip={
                    reviewPending
                      ? 'Changes from your masters are waiting for review'
                      : 'Review changes from your masters'
                  }
                  aria-label={
                    reviewPending
                      ? `Master updates, ${reviewPending} ${
                          reviewPending === 1 ? 'change' : 'changes'
                        } waiting`
                      : 'Master updates'
                  }
                  active={specReviewing}
                  onClick={() => (specReviewing ? reviewLeave() : startReview())}
                  badge={
                    reviewPending ? (
                      <StatusBadge status="waiting" label={String(reviewPending)} variant="info" />
                    ) : undefined
                  }
                />
              ),
            },
          ]
        : []),
      {
        id: 'history',
        label: 'Snapshots and history',
        section: 'document',
        node: (
          <ToolbarButton
            icon={HistoryIcon}
            label="History"
            tooltip={reviewing ? REVIEW_LOCK_REASON : undefined}
            disabled={reviewing}
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setHistoryAnchor({ left: r.left, bottom: r.bottom, right: r.right });
            }}
          />
        ),
      },
      {
        id: 'export',
        label: 'Export…',
        section: 'document',
        node: (
          <ToolbarButton
            icon={Download}
            label="Export…"
            variant="outline"
            onClick={() => setExportOpen(true)}
            disabled={!doc.exportCtx}
          />
        ),
      },
      {
        id: 'issue',
        label: doc.phase === 'design' ? 'Issue…' : 'Issuances…',
        section: 'document',
        node: (
          <ToolbarButton
            icon={Stamp}
            label={doc.phase === 'design' ? 'Issue…' : 'Issuances…'}
            variant="outline"
            onClick={() => setDeskOpen(true)}
            tooltip="Issuances — the manual's baselines and addenda"
          />
        ),
      },
    ];
  }, [
    spec,
    editor,
    canEdit,
    viewState,
    zoom,
    onSave,
    openLevelStyle,
    undoTimeline,
    draft.undo,
    draft.redo,
    draft.canUndo,
    draft.canRedo,
    doc.exportCtx,
    doc.phase,
    doc.collabPending,
    related.insert,
    reviewing,
    specReviewing,
    canViewMasterUpdates,
    reviewPending,
    reviewLeave,
    startReview,
    canPublish,
    specPending,
    specEverPublished,
  ]);

  if (doc.loading) {
    return (
      <div className="dsg-root dsg-loading" aria-busy="true">
        <div className="dsg-skeleton-rail" />
        <div className="dsg-skeleton-sheet">
          <span className="sr-only">Opening the spec…</span>
        </div>
      </div>
    );
  }

  if (doc.error || !spec) {
    return (
      <div className="dsg-root dsg-error-root">
        <EmptyState
          title="This spec could not be opened"
          subtitle={doc.error || 'It may have been removed from the project.'}
          action={{ label: 'Back to the project', onClick: onBack }}
        />
      </div>
    );
  }

  // The breadcrumb shows the section number the way this forma prints it.
  const csi =
    formatSpecNumber(spec.csiNumber || '', doc.forma?.docSettings?.specNumberFormat) ||
    spec.csiNumber ||
    '—';

  // A live shared document holds the paper read-only until it has synced and
  // been seeded (useDesignerCollab) - a keystroke must not land in a document
  // that is about to be replaced.
  const paperEditable = canEdit && !viewState && !doc.collabPending && !reviewing;

  return (
    <div className="dsg-root">
      <header className="dsg-topbar" ref={topBarRef}>
        {/* ── Row 1: identity. Where am I, and what state is this document
            in. Nothing else lives here. ── */}
        <div className="dsg-idrow">
          <button type="button" className="dsg-back" onClick={() => void leave()}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Projects
          </button>
          <nav className="dsg-crumb" aria-label="Breadcrumb">
            <span className="dsg-crumb-project">
              {doc.projectNumber ? `${doc.projectNumber} · ` : ''}
              {doc.projectName || 'Project'}
            </span>
            <span className="dsg-crumb-sep" aria-hidden="true">
              ▸
            </span>
            <span className="dsg-crumb-spec">
              <strong>{csi}</strong> {spec.title || 'Untitled section'}
            </span>
          </nav>

          <div className="dsg-idrow-status">
            {/* Which life the document is living. */}
            <span
              className={`dsg-mode-chip dsg-mode-${doc.phase}`}
              title={
                doc.phase === 'design'
                  ? 'Not yet issued — edits are quiet; snapshots keep them recoverable'
                  : doc.baseline
                    ? `Changes since “${doc.baseline.name}” render as revision marks`
                    : 'Issued — this section was added after the current issuance'
              }
            >
              {doc.phase === 'design' ? 'Design phase' : doc.baseline?.name || 'Issued'}
            </span>
            {/* Whose forma the paper's formatting lands on. */}
            <span
              className={`dsg-scope-chip${draft.isProjectCopy ? ' is-project' : ''}`}
              title={
                draft.isProjectCopy
                  ? 'This project has its own forma copy — formatting edits apply to this project only'
                  : 'This spec still prints with a master forma. Your first formatting edit forks a copy for this project; the master stays untouched.'
              }
            >
              {draft.isProjectCopy ? 'Project forma' : 'Master forma'}
            </span>
            {formaFlushing && (
              <span className="dsg-save dsg-save-saving" role="status" aria-live="polite">
                Saving your forma edits…
              </span>
            )}
            {(draft.live || draft.dirty || draft.saving) && (
              <FormaChip
                state={draft.chip}
                title="The project forma's shared draft — exports use the published forma until Publish"
              />
            )}
            {draft.isProjectCopy && draft.live && canPublishForma && (
              <>
                {draft.dirty && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={reviewing || draft.saving}
                    onClick={() => setFormaDiscardOpen(true)}
                  >
                    Discard draft
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  disabled={reviewing || draft.saving || !draft.dirty}
                  title={
                    reviewing
                      ? REVIEW_LOCK_REASON
                      : draft.dirty
                        ? 'Make this forma the one every export uses'
                        : 'Nothing to publish — the forma is up to date'
                  }
                  onClick={() => setFormaPublishOpen(true)}
                >
                  Publish forma
                </Button>
              </>
            )}
            {draft.peers.length > 0 && <PeerChips peers={draft.peers} />}
            <SaveIndicator state={doc.saveState} at={doc.lastSavedAt} publish={publishStanding} />
            <PeerChips peers={doc.peers} />
          </div>
        </div>

        {/* ── Row 2: the panel buttons, the tools, the Workspace menu. ── */}
        <div ref={toolbarRef}>
          <DesignerToolbar
            items={toolbarItems}
            panels={panelEntries}
            onPanelClick={onPanelClick}
            onPanelDragStart={setPanelDragId}
            onPanelDragEnd={onPanelDragEnd}
            dockLocked={dockLocked}
            onToggleDockLocked={toggleDockLocked}
            onResetLayout={() => dockHandleRef.current?.resetLayout()}
            onSetFirmDefault={setFirmDefault}
            canSetFirmDefault={canSetFirmDefault}
            userId={userId}
            pageView={paged ? 'pages' : 'continuous'}
            onPageView={(v) => setPaged(v === 'pages')}
          />
          {/* Forma Studio's Format Bar — the same bulk editor for whatever
              levels / tokens / End of Section the one selection holds. */}
          <FormaFormatBar
            forma={paperForma}
            keys={sel.keys}
            edit={draft.edit}
            canEdit={paperEditable}
          />
        </div>
      </header>

      <div className="dsg-body">
        <main className="dsg-main">
          {!canEdit && !viewState && (
            <div className="dsg-readonly-banner" role="status">
              You have view access to this spec. Ask a project publisher for edit rights to make
              changes.
            </div>
          )}
          {viewState && (
            <div className="dsg-viewing-banner" role="status">
              <span className="dsg-viewing-label">{viewState.label} — read-only</span>
              <span className="dsg-viewing-actions">
                {viewing?.kind === 'snapshot' && canEdit && (
                  <button
                    type="button"
                    className="dsg-btn"
                    onClick={() => {
                      pendingRestoreRef.current = viewing.snap;
                      setViewing(null);
                    }}
                  >
                    Restore this version
                  </button>
                )}
                {viewing?.kind === 'issuance' && (
                  <button
                    type="button"
                    className="dsg-btn"
                    disabled={exportingIssuance}
                    onClick={() => void exportIssuance(viewing.mi)}
                  >
                    {exportingIssuance ? 'Exporting…' : 'Export as issued'}
                  </button>
                )}
                <button type="button" className="dsg-btn" onClick={() => setViewing(null)}>
                  Back to the live document
                </button>
              </span>
            </div>
          )}

          {formaReviewing && (
            <ReviewBar
              lane="forma"
              mode="review"
              index={formaReview.walker.index}
              total={formaReview.items.length}
              decision={formaCurrentId ? formaReview.decisions[formaCurrentId] : null}
              onPrev={formaReview.walker.prev}
              onNext={formaReview.walker.next}
              onAccept={() => {
                if (!formaCurrentId) return;
                formaReview.decide(formaCurrentId, 'accept');
                formaReview.walker.next();
              }}
              onReject={() => {
                if (!formaCurrentId) return;
                formaReview.decide(formaCurrentId, 'reject');
                formaReview.walker.next();
              }}
              onAcceptAll={formaReview.acceptAllRemaining}
              canUndoAcceptAll={formaReview.canUndoAcceptAll}
              onUndoAcceptAll={formaReview.undoAcceptAll}
              onFinish={() =>
                void formaReview.finish().then((ok) => {
                  // The forma lane is entered from the project's Specs tab —
                  // finishing it returns there.
                  if (ok) void leave();
                })
              }
              onLeave={formaReviewLeave}
              canDecide={canPublishForma}
              remaining={formaReview.items.filter((c) => !formaReview.decisions[c.id]).length}
              busy={formaReview.busy}
              error={formaReview.error}
              note="Accepted changes go into the forma draft — Publish forma to use them"
            />
          )}
          {specReviewing && (
            <ReviewBar
              lane="master"
              mode="review"
              index={review.walker.index}
              total={review.items.length}
              decision={reviewCurrent ? review.decisions[reviewCurrent.id] : null}
              onPrev={review.walker.prev}
              onNext={review.walker.next}
              onAccept={() => {
                if (!reviewCurrent) return;
                review.decide(reviewCurrent.id, 'accept');
                review.walker.next();
              }}
              onReject={() => {
                if (!reviewCurrent) return;
                review.decide(reviewCurrent.id, 'reject');
                review.walker.next();
              }}
              onAcceptAll={review.acceptAllRemaining}
              canUndoAcceptAll={review.canUndoAcceptAll}
              onUndoAcceptAll={review.undoAcceptAll}
              onFinish={() => void review.finish()}
              onLeave={reviewLeave}
              canDecide={review.canDecide && !!review.row}
              remaining={review.items.filter((c) => !review.decisions[c.id]).length}
              busy={review.busy}
              error={review.error}
              note={
                review.canDecide
                  ? REVIEW_LOCK_REASON
                  : 'Publish permission needed to accept changes'
              }
            />
          )}

          <DesignerWorkspace
            modules={
              canViewModules ? (
                <ModulesPanel
                  modules={moduleInsert.modules}
                  loading={moduleInsert.loading}
                  error={moduleInsert.error}
                  canInsert={paperEditable}
                  insertHint="This section is read-only right now"
                  busyModuleId={moduleInsert.busyModuleId}
                  onInsert={moduleInsert.insertModule}
                  onRetry={moduleInsert.reload}
                  userName={userName}
                  toast={toast}
                  chips={moduleInsert.chips}
                  armedKey={moduleInsert.armedKey}
                  onArm={moduleInsert.arm}
                  onDiscard={moduleInsert.discard}
                  inDocument={moduleInsert.inDocument}
                  onReveal={moduleInsert.revealInsertion}
                  revealInsertId={moduleInsert.panelReveal?.insertId ?? null}
                  revealSeq={moduleInsert.panelReveal?.seq ?? 0}
                  onRemove={moduleInsert.requestRemoveByInsertId}
                  onReview={moduleInsert.reviewInsertion}
                  reviewFor={moduleInsert.reviewFor}
                  onReviewDecide={moduleInsert.decideReview}
                  onReviewCancel={moduleInsert.closeReview}
                />
              ) : null
            }
            isDark={isDark}
            userId={userId || 'anon'}
            locked={dockLocked}
            doc={doc}
            draft={draft}
            canEdit={paperEditable}
            sections={doc.sections}
            activeSectionId={projectSpecId}
            onOpenSection={(id) => void openSection(id)}
            onManageSections={onManageSections}
            editor={editor}
            onJump={jumpTo}
            sel={sel}
            onOpenPanel={openTool}
            pageVariant={effPageVariant}
            onPageVariant={setPageVariant}
            onDragState={setTokenDrag}
            dragActive={tokenDrag}
            toast={toast}
            floatingTools={floatingTools}
            onCloseFloating={closeFloating}
            onDockFloating={dockFloating}
            dropdown={dropdown}
            onCloseDropdown={() => setDropdown(null)}
            onFloatDropdown={floatTool}
            onUndock={undockToFloat}
            panelDragId={panelDragId}
            firmLayoutKey={DESIGNER_FIRM_LAYOUT_KEY}
            onDockPanelsChange={setDockPanels}
            onDockReady={(h) => {
              dockHandleRef.current = h;
              setDockReady(true);
            }}
            canvas={(wiring) =>
              viewState?.missing ? (
                <div className="dsg-viewing-missing">
                  <EmptyState
                    title="Not part of this issuance"
                    subtitle={`${csi} ${spec.title || ''} was added to the manual after ${
                      viewing?.kind === 'issuance' ? viewing.mi.name : 'this issuance'
                    }. Pick another section in the rail, or go back to the live document.`}
                    action={{
                      label: 'Back to the live document',
                      onClick: () => setViewing(null),
                    }}
                  />
                </div>
              ) : (
                <DesignerCanvas
                  paged={paged}
                  reviewing={reviewing}
                  specId={viewState ? `${projectSpecId}:${viewState.key}` : projectSpecId}
                  blocks={viewState ? viewState.blocks || [] : spec.coreContent || []}
                  forma={viewState ? doc.forma : paperForma}
                  specNumber={spec.csiNumber || ''}
                  specName={spec.title || ''}
                  projectMeta={viewState ? doc.projectMeta : paperMeta}
                  zoom={zoom}
                  onZoomDelta={(step) => setZoomPersisted(zoom + step)}
                  onChange={viewState ? () => undefined : onChange}
                  onSave={onSave}
                  onEscape={() => topBarRef.current?.querySelector('button')?.focus()}
                  onEditorReady={setEditor}
                  editable={paperEditable}
                  // The bands are live objects whenever the paper is live.
                  design={paperEditable ? wiring : undefined}
                  onLevelStyle={openLevelStyle}
                  moduleMenu={{
                    selectInsertion: moduleInsert.selectInsertion,
                    removeInsertion: moduleInsert.requestRemoveInsertion,
                  }}
                  baselineBlocks={viewState ? null : doc.baselineBlocks}
                  revisionSettings={doc.revisionSettings}
                  relatedSections={related.store}
                  onInsertRelatedSections={related.insert}
                  collab={viewState ? null : doc.collab}
                  reviewHighlights={viewState ? null : formaHighlights}
                />
              )
            }
          />
        </main>
        {formaReviewing && (
          <ReviewPanel
            title="Forma updates"
            items={formaReview.items}
            decisions={formaReview.decisions}
            currentId={formaCurrentId}
            onJump={formaReview.walker.setCurrentId}
            rows={formaReview.rows}
            activeRowId={formaReview.row?.id ?? null}
            onPickRow={formaReviewStart}
            reconciled={formaReview.reconciled}
            reconciledCount={formaReview.row?.reconciledCount ?? 0}
            loading={formaReview.loading}
            loadError={formaReview.loadError}
            onRetry={formaReview.reload}
            describe={describeFormaChange}
            describeNote={describeFormaNote}
            receiptLabel="already in this forma"
            emptyTitle="This forma is up to date with its master."
          />
        )}
        {specReviewing && (
          <ReviewPanel
            title="Master updates"
            items={review.items}
            decisions={review.decisions}
            currentId={reviewCurrent?.id ?? null}
            onJump={review.walker.setCurrentId}
            rows={review.rows}
            activeRowId={review.row?.id ?? null}
            onPickRow={reviewStart}
            reconciled={review.reconciled}
            reconciledCount={review.row?.reconciledCount ?? 0}
            upstreamPending={review.upstreamPending}
            loading={review.loading}
            loadError={review.loadError}
            onRetry={review.reload}
          />
        )}
      </div>

      <MasterUpdatesReminder
        open={reminderOpen}
        count={reviewPending}
        onReviewNow={() => {
          setReminderOpen(false);
          startReview();
        }}
        onLater={() => setReminderOpen(false)}
        onDontShowAgain={() => {
          dismissReminder(projectSpecId, review.rows);
          setReminderOpen(false);
        }}
      />

      <RelatedSectionsPicker editor={editor} store={related.store} />

      {/* Inserting a module: the drop line, and the two confirmations. */}
      {moduleInsert.overlay}

      {/* The find bar hangs off the toolbar, whether Ctrl+F or the button
          opened it — so it is never unreachable because a button is unpinned. */}
      <FindReplaceHost editor={editor} canEdit={paperEditable} anchorRef={toolbarRef} />

      {/* ── Review overlays ── */}
      {marksAnchor && !reviewing && (
        <RevisionStylesPanel
          anchor={marksAnchor}
          settings={doc.revisionSettings}
          baseline={doc.baseline}
          baselineLabel={doc.baseline?.name}
          canEdit={canEdit}
          onChange={(next) =>
            void doc
              .saveRevisionSettings(next)
              .catch(() => toast('Could not save mark styles', true))
          }
          onClose={() => setMarksAnchor(null)}
        />
      )}
      {historyAnchor && !reviewing && (
        <HistoryPanel
          anchor={historyAnchor}
          spec={spec}
          canEdit={canEdit}
          onView={(snap) => {
            if (reviewing) return;
            setViewing({ kind: 'snapshot', snap });
          }}
          onRestore={(snap) => {
            if (reviewing) return;
            pendingRestoreRef.current = snap;
            setViewing(null);
          }}
          onClose={() => setHistoryAnchor(null)}
        />
      )}
      <IssuanceDesk
        open={deskOpen}
        onClose={() => setDeskOpen(false)}
        projectName={doc.projectName}
        specs={doc.exportCtx?.allSpecs || []}
        manualIssuances={doc.manualIssuances}
        exportCtx={doc.exportCtx}
        canIssue={mayIssue}
        busy={exportingIssuance}
        onIssue={issueAndExport}
        onViewIssuance={(mi) => {
          setDeskOpen(false);
          if (reviewing) {
            toast(REVIEW_LOCK_REASON, true);
            return;
          }
          setViewing({ kind: 'issuance', mi });
        }}
        onExportIssuance={(mi) => void exportIssuance(mi)}
        toast={toast}
      />
      <ConfirmDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title="Publish this section"
        description={publishConfirmBody(doc.peers)}
        confirmLabel="Publish"
        destructive={false}
        busy={publishBusy}
        onConfirm={() => void confirmPublish()}
      />
      <ConfirmDialog
        open={formaPublishOpen}
        onOpenChange={setFormaPublishOpen}
        title="Publish this forma?"
        description={formaPublishConfirmBody(formaPeerNames(draft.peers))}
        confirmLabel="Publish"
        destructive={false}
        busy={draft.saving}
        onConfirm={() => void confirmFormaPublish()}
      />
      <ConfirmDialog
        open={formaDiscardOpen}
        onOpenChange={setFormaDiscardOpen}
        title="Discard this forma draft?"
        description={formaDiscardDraftBody(formaPeerNames(draft.peers))}
        confirmLabel="Discard draft"
        onConfirm={() => {
          setFormaDiscardOpen(false);
          draft
            .discardDraft()
            .catch((err: unknown) =>
              toast(
                'Could not discard the draft: ' +
                  (err instanceof Error ? err.message : String(err ?? 'unknown')),
                true
              )
            );
        }}
      />
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        currentSpecId={projectSpecId}
        specs={doc.exportCtx?.allSpecs || []}
        exportCtx={doc.exportCtx}
        hasBaseline={doc.phase === 'issued'}
        toast={toast}
      />
    </div>
  );
}

export default Designer;
